import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SpinnerVisibilityService } from 'ng-http-loader';
import { switchMap, of } from 'rxjs';
import { CategoryManagementService } from '../../services/category-management.service';
import { AdminCategory } from '../../model/category-management-dto';

@Component({
  selector: 'app-category-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule
  ],
  templateUrl: './category-dialog.component.html',
  styleUrl: './category-dialog.component.scss'
})
export class CategoryDialogComponent implements OnInit {

  form: FormGroup;
  category?: AdminCategory;
  imageFile: File | null = null;
  imagePreview: string | null = null;
  errorMessage: string | null = null;

  constructor(
    private fb: FormBuilder,
    private service: CategoryManagementService,
    private spinner: SpinnerVisibilityService,
    public dialogRef: MatDialogRef<CategoryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { category?: AdminCategory }
  ) {
    this.category = data.category;
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      name: [this.category?.categoryName || '', Validators.required],
      iconName: [this.category?.iconName || '', Validators.required],
      backgroundColor: [this.normalizeColor(this.category?.backgroundColor), Validators.required],
      active: [this.category ? this.category.active : true]
    });
    this.imagePreview = this.category?.imageUrl || null;
  }

  get isEdit(): boolean {
    return !!this.category;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      this.imageFile = input.files[0];
      const reader = new FileReader();
      reader.onload = () => this.imagePreview = reader.result as string;
      reader.readAsDataURL(this.imageFile);
    }
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (!this.isEdit && !this.imageFile) {
      this.errorMessage = 'La imagen de la categoría es requerida';
      return;
    }

    this.errorMessage = null;
    this.spinner.show();

    //Primero se sube la imagen (si hay nueva) y luego se guarda la categoría
    const upload$ = this.imageFile
      ? this.service.uploadImage(this.imageFile, 'category-icons')
      : of(null);

    upload$.pipe(
      switchMap((uploadResponse: any) => {
        const imageFileId = uploadResponse?.data?.fileId;
        const value = this.form.value;
        const body: any = {
          name: value.name,
          iconName: value.iconName,
          backgroundColor: this.stripHash(value.backgroundColor),
          active: value.active
        };
        if (imageFileId) {
          body.imageFileId = imageFileId;
        }
        return this.isEdit
          ? this.service.updateCategory(this.category!.categoryId!, body)
          : this.service.createCategory(body);
      })
    ).subscribe({
      next: () => {
        this.spinner.hide();
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.spinner.hide();
        console.error(err);
        this.errorMessage = err?.error?.message || 'Error guardando la categoría';
      }
    });
  }

  private normalizeColor(color?: string): string {
    if (!color) {
      return '#cccccc';
    }
    return color.startsWith('#') ? color : `#${color}`;
  }

  private stripHash(color: string): string {
    return (color || '').replace('#', '').toUpperCase();
  }
}
